import {
  Geo,
  AssetLoader,
  QueryString,
  TopLevelDomainExtractor,
  Logger,
  LoggerTransformer,
} from "@identity/utils";
import { ConsentProviderInterface } from "./consent-provider-interface";
import { Consents } from "../types";
import { gdprCountries } from "../../../utils/gdpr-countries";
import { COOKIES, WINDOW } from "@identity/symbols";
import { inject, injectWithTransform, singleton } from "tsyringe";

const ONE_TRUST_DOMAIN_IDS = {
  ".fanatical.com": "0ab0e172-dc9c-44b9-8a58-c80d9ba4fae0",
  ".futhead.com": "82c642a4-d3a9-4a78-a310-21a7b49ab17b",
  ".gamespot.com": "73588546-b116-4dbc-ab64-9db97e11fc0d",
  ".metacritic.com": "50e16f1d-a929-4cc0-80a7-3c5c303eea6a",
  ".muthead.com": "fb34a16e-696d-4748-af9f-96f3b9e2b4da",
  ".tvguide.com": "ffc22311-f4da-4e03-a1ad-32aecd522c5b",
  ".fandom.com": "dea70a1b-c82d-4fe0-86ff-5e164b0a6022",
};
const ONE_TRUST_LIBRARIES = [
  {
    url: "https://cdn.cookielaw.org/scripttemplates/otSDKStub.js",
    options: {},
  },
  {
    url: "https://cdn.cookielaw.org/opt-out/otCCPAiab.js",
    options: {
      charSet: "UTF-8",
      type: "text/javascript",
      "ccpa-opt-out-ids": "C0004",
      "ccpa-opt-out-geo": "us",
      "ccpa-opt-out-lspa": "false",
    },
  },
  {
    url: "https://cdn.cookielaw.org/consent/tcf.stub.js",
    options: {
      charSet: "UTF-8",
    },
  },
];

@singleton()
export class OneTrust implements ConsentProviderInterface {
  consentGiven: Promise<Consents>;
  resolveConsent: (consent: Consents) => void;
  private trackingGroup = "C0004:1";
  private testMode = false;

  constructor(
    @inject(COOKIES) private cookiesManager: CookiesManager,
    @inject(WINDOW) private window: Window,
    @inject(AssetLoader) private assetLoader: AssetLoader,
    @inject(Geo) private geo: Geo,
    @injectWithTransform(Logger, LoggerTransformer, "OneTrust")
    private readonly logger: Logger,
  ) {
    this.replaceDoNotSellLink();
    this.testMode = !!QueryString.get("ot_test");
    this.consentGiven = new Promise((res) => (this.resolveConsent = res));
    const modalClosed = this.oneTrustModalClosed();
    if (modalClosed) {
      this.consentsUpdated();
    }
    if (!this.oneTrustLoaded()) {
      this.loadOneTrustScripts();
    }
    if (this.geo.getCountryCode() === "us") {
      this.consentsUpdated();
    }
    this.subscribe();
  }

  private oneTrustLoaded(): boolean {
    return !!(this.window.OneTrust || this.window.OneTrustStub);
  }

  private replaceDoNotSellLink(): void {
    try {
      const doNotSellMyInfoLink = document.querySelector(
        'a[href$="do-not-sell-my-info"]',
      ) as HTMLAnchorElement;
      doNotSellMyInfoLink.href = "#";
      doNotSellMyInfoLink.classList.add("ot-sdk-show-settings");
    } catch (e) {
      this.logger.error("Could not prepare Opt-out button", e);
    }
  }

  private oneTrustModalClosed(): boolean {
    const oneTrustCookie = this.cookiesManager.get("OptanonAlertBoxClosed");
    return oneTrustCookie && !!oneTrustCookie;
  }

  private subscribe(): void {
    const oldOptanonWrapper = this.window.OptanonWrapper;
    this.window.OptanonWrapper = () => {
      const modalClosed = this.oneTrustModalClosed();
      if (modalClosed || this.geo.getCountryCode() === "us") {
        this.consentsUpdated();
        oldOptanonWrapper?.();
      }
    };
  }

  private oneTrustCookieExists() {
    return !!this.cookiesManager.get("OptanonConsent");
  }

  private readTrackingConsents(): boolean {
    const cookie = this.cookiesManager.get("OptanonConsent");
    const groups = cookie?.split("groups=").pop()?.split(";")[0] || "";
    if (!groups.length || !cookie.includes("groups")) {
      return this.geo.getCountryCode() === "us";
    }
    return groups.includes(this.trackingGroup);
  }

  private consentsUpdated(): void {
    if (!this.oneTrustCookieExists()) {
      return;
    }
    const location = this.geo.getCountryCode();
    // In GDPR user gives us consent. In CCPA user gives us a signal to OptOut.
    // Therefore in US we assume, that signal = true, means that user opted-out
    const trackingConsent =
      location === "us"
        ? !this.readTrackingConsents()
        : this.readTrackingConsents();
    if (location) {
      this.resolveConsent({
        gdprConsent: gdprCountries.includes(location) ? trackingConsent : true,
        geoRequiresConsent: gdprCountries.includes(location),
        geoRequiresSignal: location === "us",
        ccpaSignal: location === "us" ? trackingConsent : false,
      });
    }
  }

  private loadOneTrustScripts() {
    this.logger.info("Loading OneTrust scripts");
    ONE_TRUST_LIBRARIES.forEach((library) => {
      this.assetLoader.loadScript(library.url, "text/javascript", false, null, {
        ...library.options,
        "data-domain-script": this.getDomainId(),
      });
    });
  }

  private getDomainId() {
    const tld = TopLevelDomainExtractor.extract(this.window.location.hostname);
    const domainId =
      ONE_TRUST_DOMAIN_IDS[tld] || ONE_TRUST_DOMAIN_IDS[".fandom.com"];

    return this.testMode ? `${domainId}-test` : domainId;
  }
}
